import { yieldToMain } from '@src/quince-site-common/src/utils/yieldToMain'

type TelemetryFanOut = () => void

const pendingTelemetryFanOuts = new Set<TelemetryFanOut>()
let isPageHideListenerAttached = false

/**
 * Experiment-gated (`all__telemetry_fan-out-defer__web`). The bucket is read once from Statsig by
 * the user-aware provider and pushed in here, so the hot emit path never touches Statsig. Control —
 * the default, and the value on the server and before the client resolves — keeps fan-out fully
 * synchronous, so a rollback is a bucket flip with no code change.
 */
let isFanOutDeferralEnabled = false

export const setTelemetryFanOutDeferralEnabled = (enabled: boolean): void => {
  isFanOutDeferralEnabled = enabled
}

const flushPendingTelemetryFanOuts = () => {
  isPageHideListenerAttached = false
  const pendingFanOuts = Array.from(pendingTelemetryFanOuts)
  pendingTelemetryFanOuts.clear()
  pendingFanOuts.forEach(fanOut => {
    fanOut()
  })
}

const removePageHideListenerWhenIdle = () => {
  if (pendingTelemetryFanOuts.size > 0 || !isPageHideListenerAttached || typeof window === 'undefined') return
  window.removeEventListener('pagehide', flushPendingTelemetryFanOuts, false)
  isPageHideListenerAttached = false
}

export const shouldDeferTelemetryFanOut = (): boolean => isFanOutDeferralEnabled

export const scheduleTelemetryFanOut = (fanOut: TelemetryFanOut, shouldDefer: boolean): void => {
  if (!shouldDefer) {
    fanOut()
    return
  }

  pendingTelemetryFanOuts.add(fanOut)
  if (typeof window !== 'undefined' && !isPageHideListenerAttached) {
    window.addEventListener('pagehide', flushPendingTelemetryFanOuts, { once: true })
    isPageHideListenerAttached = true
  }

  // Yield directly instead of via `runAfterInteractionPaint`: that helper is itself gated on the
  // separate INP interaction-scheduling gate, so routing through it would make this experiment a
  // no-op whenever that gate is off. `yieldToMain` is the ungated primitive it wraps.
  void (async () => {
    await yieldToMain()
    if (!pendingTelemetryFanOuts.delete(fanOut)) return
    removePageHideListenerWhenIdle()
    fanOut()
  })()
}
